import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { species } from '@/data';
import { conservationLabels, type ConservationStatus } from '@/types';
import { useProgress } from '@/hooks/useProgress';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

type FilterOption = 'all' | 'freshwater' | 'saltwater' | 'both' | 'viewed' | 'not-viewed';

export function Species() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterOption>('all');
  const { progress, hasViewedSpecies } = useProgress();

  const filteredSpecies = useMemo(() => {
    return species.filter((otter) => {
      // Search filter
      const searchLower = search.toLowerCase();
      const matchesSearch =
        search === '' ||
        otter.commonName.toLowerCase().includes(searchLower) ||
        otter.scientificName.toLowerCase().includes(searchLower);

      // Type filter
      let matchesFilter = true;
      if (filter === 'freshwater') {
        matchesFilter = otter.habitat.waterType === 'freshwater';
      } else if (filter === 'saltwater') {
        matchesFilter = otter.habitat.waterType === 'saltwater';
      } else if (filter === 'both') {
        matchesFilter = otter.habitat.waterType === 'both';
      } else if (filter === 'viewed') {
        matchesFilter = hasViewedSpecies(otter.id);
      } else if (filter === 'not-viewed') {
        matchesFilter = !hasViewedSpecies(otter.id);
      }

      return matchesSearch && matchesFilter;
    });
  }, [search, filter, hasViewedSpecies]);

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-800 dark:text-white mb-2">
          Otter Species
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Explore all 13 species of otters from around the world
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search species..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-otter-500 focus:border-transparent"
        />
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide -mx-4 px-4">
        {[
          { value: 'all', label: 'All' },
          { value: 'freshwater', label: '💧 Freshwater' },
          { value: 'saltwater', label: '🌊 Saltwater' },
          { value: 'both', label: '🔄 Both' },
          { value: 'viewed', label: '✓ Viewed' },
          { value: 'not-viewed', label: '○ Not Viewed' },
        ].map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value as FilterOption)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === option.value
                ? 'bg-otter-500 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between mb-4 text-sm text-slate-500 dark:text-slate-400">
        <span>{filteredSpecies.length} species</span>
        <span>{progress.speciesViewed.length}/{species.length} viewed</span>
      </div>

      {/* Species Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 gap-3"
      >
        {filteredSpecies.map((otter) => {
          const viewed = hasViewedSpecies(otter.id);

          return (
            <motion.div key={otter.id} variants={itemVariants}>
              <Link to={`/species/${otter.id}`}>
                <Card hoverable padded={false} className="overflow-hidden h-full">
                  {/* Image */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-otter-100 to-water-100 dark:from-otter-900 dark:to-water-900 relative overflow-hidden">
                    {otter.images && otter.images.length > 0 ? (
                      <img
                        src={otter.images[0].url}
                        alt={otter.images[0].alt || otter.commonName}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-5xl">
                        🦦
                      </div>
                    )}
                    {/* Conservation Status Badge */}
                    <div className="absolute top-2 right-2">
                      <Badge
                        variant="conservation"
                        conservationStatus={otter.conservationStatus}
                        className="text-[10px] px-1.5 py-0.5"
                      >
                        {otter.conservationStatus}
                      </Badge>
                    </div>
                    {/* Viewed indicator */}
                    {viewed && (
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h3 className="font-semibold text-slate-800 dark:text-white text-sm leading-tight mb-1">
                      {otter.commonName}
                    </h3>
                    <p className="text-xs text-slate-500 italic mb-2 truncate">
                      {otter.scientificName}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>
                        {otter.habitat.waterType === 'freshwater' && '💧'}
                        {otter.habitat.waterType === 'saltwater' && '🌊'}
                        {otter.habitat.waterType === 'both' && '💧🌊'}
                      </span>
                      <span className="truncate">
                        {otter.characteristics.weight.min}-{otter.characteristics.weight.max} kg
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </motion.div>

      {filteredSpecies.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-slate-400">
            No species found matching your search.
          </p>
        </div>
      )}
    </div>
  );
}
